export const ATLAS_DELIVERY_CHECKLIST = [
  "Confirm the original manufacturer, procurement and branding permissions, then freeze the bill of materials.",
  "Specify cameras, compute, end effectors, control interfaces, software versions and licenses.",
  "Verify the workspace, emergency stop, collision controls and communication-loss response before experiments.",
  "Validate recording, calibration, timing and task performance on the delivered configuration.",
];

export const ATLAS_CONFIG_NOTE =
  "Project integration configuration. Images are concept illustrations, not delivery photographs or engineering CAD. Reference specifications do not establish completed integration, proprietary ownership or brand authorization. Final hardware and performance are subject to project validation and acceptance.";

export const CONCEPT_IMAGE_NOTE = "Concept illustration; not a photograph of the delivered system.";

export const GPU_OPTIONS_NOTE =
  "RTX 4070 / 4080 / 4090 / 5070 / 5080 / 5090 workstation options. Power, cooling, installation and software compatibility require engineering review; high-power GPUs may use an external workstation.";

export const THOR_SPEC_NOTE =
  "Nominal specifications from the September 2026 v1.1 product documentation. Runtime, power, weight and synchronization depend on the confirmed configuration and test conditions. The 3 us IMU-to-frame figure is internal device alignment, not end-to-end synchronization with other devices.";

export const THOR_SHARED_SPEC_GROUPS = [
  {
    title: "Imaging and video",
    rows: [
      ["Resolution / frame rate", "1920 x 1080, 2 MP per camera, 30 FPS"],
      ["Sensor options", "AR0234 / AR0235 / SC233GS / SC235GS, subject to configuration"],
      ["Horizontal / vertical field of view", "60-210 degrees; configurable"],
      ["TV distortion", "2-20%; software correction supported"],
      [
        "Image controls",
        "Brightness, contrast, grayscale and switchable 3D noise reduction via Web UI / API",
      ],
      ["Anti-flicker", "50 / 60 Hz, automatic or manual"],
      ["Exposure / bit depth", "Automatic exposure up to 15 ms / 8 bit"],
      ["Stereo baseline", "60 mm standard; mechanical customization available"],
      ["Encoding", "Motion JPEG / H.264 / H.265"],
      ["Bitrate", "Automatic or user-adjustable 14-28 Mbps"],
    ],
  },
  {
    title: "Inertial sensing and calibration",
    rows: [
      ["IMU", "300 Hz"],
      ["IMU-to-frame signal alignment", "3 us"],
      [
        "Factory calibration",
        "Optical axes, camera intrinsics and extrinsics, IMU temperature and orientation",
      ],
      ["Baseline mounting tolerance", "+/-0.05 mm (3 sigma)"],
      ["Mounting surface flatness", "<=0.02 mm / 60 mm"],
      ["Locating pin / hole", "H7 / g6 clearance fit"],
      ["Sensor roll", "+/-0.1 degrees"],
      ["Optical-axis perpendicularity", "<=0.2 degrees"],
    ],
    note: "Keep calibration files tied to the device, firmware and mounting configuration. Verify cross-device clock mapping separately.",
  },
  {
    title: "Recording and field operation",
    rows: [
      ["Local recording", "Synchronized video and IMU on SD card"],
      ["USB output", "UVC video + IMU"],
      ["Recording control", "Button, USB or Wi-Fi commands; silent recording supported"],
      [
        "Status alerts",
        "Missing card, full card and dropped frames; configurable lights, buzzer, USB and Wi-Fi notifications",
      ],
      ["Audio", "Dual microphones, 48 kHz, basic AEC / ANS / AGC"],
      [
        "Capture assistance",
        "Configurable hand-out-of-frame alerts, automatic face masking and static-scene detection",
      ],
    ],
  },
  {
    title: "Connectivity and maintenance",
    rows: [
      ["Interfaces", "USB 2.0 High Speed / 5.8 GHz Wi-Fi"],
      [
        "Preview / configuration",
        "Live Wi-Fi preview and browser configuration; some changes require restart",
      ],
      ["Clients", "iOS / Android / Windows / macOS"],
      ["Updates", "USB / Wi-Fi / SD OTA with power-loss recovery"],
      ["Identity / clock", "Unique serial number; RTC synchronization via Wi-Fi / USB"],
      ["Minimum input", "USB 5 V at 1 A; confirm the selected system power requirements"],
      ["Battery", "Replaceable lithium battery; hot-swap and operation while charging supported"],
    ],
  },
  {
    title: "Customization",
    rows: [
      [
        "Camera expansion",
        "8 / 10 / 12 cameras using 4 / 5 / 6 modules and one synchronization board; project options",
      ],
      ["Optics", "Wide-angle, fisheye or narrow-field lenses; recalibration required"],
      [
        "ISP tuning",
        "AE / AWB / gamma / sharpening / noise reduction; estimated adaptation 3-7 business days, not a delivery commitment",
      ],
      ["Mounting", "Headband, chest, pole, helmet, wrist or distributed camera arrays"],
      [
        "Cabling",
        "Flexible coaxial MIPI, internal USB expansion and remote Ethernet camera heads with cables up to 1 m",
      ],
    ],
    note: "2 / 4 / 6 cameras are standard configurations. Expanded arrays require confirmation of synchronization, weight, power, runtime and delivery scope.",
  },
];

export const THOR_PRODUCTS = [
  {
    slug: "thor-duo",
    name: "Thor-Duo",
    image: "thor-b.webp",
    subtitle: "Dual-camera visual-inertial capture",
    weight: "Approx. 180 g",
    power: "2.5 W",
    count: "2",
    modules: "1 module; no synchronization board",
    mounting: "Adjustable headband with rear counterweight battery",
    cooling: "Passive cooling",
    intro:
      "Lightweight first-person recording for natural demonstrations. A stereo camera module and IMU capture the operator viewpoint, motion and calibration context in one wearable system.",
  },
  {
    slug: "thor-quad",
    name: "Thor-Quad",
    image: "thor-c.webp",
    subtitle: "Four-camera visual-inertial capture",
    weight: "Approx. 500 g",
    power: "Approx. 5 W",
    count: "4",
    modules: "2 modules + 1 synchronization board",
    mounting: "Body straps or fixtures with an external battery",
    cooling: "Active fan cooling",
    intro:
      "Expand the view of complex manipulation. Two camera modules share hardware synchronization for multi-view capture, with flexible mounting and an external battery.",
  },
  {
    slug: "thor-hex",
    name: "Thor-Hex",
    image: "thor-a.webp",
    subtitle: "Six-camera visual-inertial capture",
    weight: "Approx. 650 g",
    power: "Approx. 7.5 W",
    count: "6",
    modules: "3 modules + 1 synchronization board",
    mounting: "Body straps or fixtures with an external battery",
    cooling: "Active fan cooling",
    intro:
      "Retain more of the task and its surroundings. Three synchronized stereo modules support broader multi-view recording for manipulation, motion and mapping research.",
  },
].map((e) => ({
  slug: e.slug,
  name: e.name,
  category: "Capture",
  subtitle: e.subtitle,
  intro: e.intro,
  image: `/assets/catalog/${e.slug}-wearing-v2.webp`,
  imageNote: "Illustrative wearing view adapted from the product reference.",
  status: "Specification-based configuration",
  metrics: [
    [`${e.count} x 1080P`, "30 FPS per camera"],
    ["300 Hz", "IMU sampling"],
    [e.weight, "Nominal system weight"],
  ],
  features: [
    [
      "Modular visual-inertial capture",
      `${e.modules}. Field of view depends on lens choice, mounting angle and calibration, not camera count alone.`,
    ],
    [
      "Calibration travels with the data",
      "Factory camera and IMU calibration supports online and offline VIO workflows. Preserve calibration versions alongside the original recordings.",
    ],
    [
      "Designed for collection sessions",
      "Combine local SD recording, USB output and Wi-Fi preview, with configurable alerts for storage, dropped frames and hands leaving the frame.",
    ],
  ],
  groups: [
    {
      title: "Standard configuration",
      rows: [
        ["Cameras", `${e.count} x 1080P`],
        ["Modules", e.modules],
        ["Power", e.power],
        ["Weight", e.weight],
        ["Nominal runtime", "6 hours; test conditions to be confirmed"],
        ["Mounting / battery", e.mounting],
        ["Cooling", e.cooling],
        [
          "Cross-module exposure synchronization",
          e.count === "2" ? "Not applicable to a single module" : "<10 us hardware alignment (specified)",
        ],
      ],
    },
    ...THOR_SHARED_SPEC_GROUPS,
  ],
  delivery: [
    "Select camera count, lens field of view, mounting and target tasks.",
    "Confirm firmware, serial numbers, calibration files and interface documentation.",
    "Check exposure alignment, dropped frames, runtime and cross-device timing in the actual collection environment.",
    "Retain raw video and inertial streams; store VIO, segmentation and annotations as traceable derived data.",
  ],
  note: THOR_SPEC_NOTE,
  related: ["thor-duo", "thor-quad", "thor-hex", "umi"].filter((t) => t !== e.slug),
}));

export const PRODUCTS = [
  ...THOR_PRODUCTS,
  {
    slug: "umi",
    name: "UMI Gripper",
    category: "Capture",
    subtitle: "Handheld manipulation demonstrations",
    intro:
      "Record human manipulation with a parallel two-finger gripper. Video, inertial measurements and opening width are paired within task episodes. The GT variant adds normal and tangential fingertip force measurements.",
    image: "/assets/catalog/umi.webp",
    imageNote: "Product reference image. GB and GT appearance depends on configuration.",
    status: "Specification-based configuration",
    metrics: [
      ["60 FPS", "Global-shutter video"],
      ["500 Hz", "IMU sampling"],
      ["100 Hz", "Width and GT tactile output"],
    ],
    features: [
      [
        "Two variants, one workflow",
        "UMI-GB records video, IMU and gripper width. UMI-GT adds fingertip tactile sensing for contact, slip and deformable-object research.",
      ],
      [
        "Record locally, archive reliably",
        "Completed episodes are written to SD before entering the NAS synchronization queue. Configured devices can continue offline until storage is full.",
      ],
      [
        "Separate collection from deployment",
        "Robot deployment requires calibration, action mapping and task evaluation. Collection alone does not establish improved learning performance.",
      ],
    ],
    groups: [
      {
        title: "GB and GT comparison",
        columns: ["Specification", "UMI-GB", "UMI-GT"],
        rows: [
          ["Video / IMU / width", "Included", "Included"],
          ["Fingertip normal / tangential force", "Not included", "Included"],
          ["Working distance", "10-30 cm", "10-30 cm"],
          ["Video", "1600 x 1200, 60 FPS, global shutter", "1600 x 1200, 60 FPS, global shutter"],
          ["IMU", "500 Hz", "500 Hz"],
          ["Gripper opening", "0-80 mm", "0-80 mm"],
          ["Repeatability", "<= +/-0.5 mm", "<= +/-0.5 mm"],
          ["Width output", "100 Hz", "100 Hz"],
          ["Recording", "Local storage / Wi-Fi / NAS sync", "Local storage / Wi-Fi / NAS sync"],
        ],
      },
      {
        title: "GT tactile sensing and data",
        rows: [
          ["Minimum detectable force", "0.3 N"],
          ["Normal force range", "0-25 N"],
          ["Tangential force range", "+/-10 N"],
          ["Tactile sampling", "100 Hz"],
          ["Safe overload", "200% FS"],
          ["Video bitrate", ">=6 Mbps"],
          ["Inertial output", "Raw acceleration and gyroscope"],
          ["Width measurement accuracy", "1 mm"],
          ["Multi-device timing residual", "Mean <=5 ms"],
        ],
        note: "Width accuracy and gripper repeatability are separate metrics. Mean timing residual is not a maximum error bound and is distinct from Thor internal signal alignment.",
      },
      {
        title: "GT hardware and package",
        rows: [
          ["Storage", "128 GB MicroSD, write-verified before delivery"],
          ["Display", "0.96-inch monochrome OLED"],
          ["Controls", "3 navigation buttons + REC button"],
          ["Power", "Built-in battery; USB charging; operation while charging"],
          ["Charging / continuous capture", "Approx. 1 hour / 2 hours"],
          ["Temperature", "0-40 C"],
          ["Weight", "Approx. 600 g per hand unit"],
          [
            "Standard GT set",
            "2 devices, 2 verified 128 GB cards and 1 operating guide; guide language to be confirmed",
          ],
          ["Deployment", "Video / IMU via USB; gripper data from the separate robot gripper system"],
        ],
        note: "GT-specific hardware, runtime and package contents do not automatically apply to GB.",
      },
      {
        title: "Recording and synchronization",
        rows: [
          ["Setup", "Configure Wi-Fi, login, NAS and task before a new recording; confirm left / right hand"],
          ["Recording", "Single-press REC to start; double-press to stop"],
          ["Archive structure", "Task / device / hand / time"],
          ["Transfer", "Wi-Fi and rsync to NAS; server, account and directory permissions required"],
          ["Cleanup", "Delete only successfully synchronized data by day; preserve complete episodes"],
          [
            "Protected records",
            "Unsynchronized, failed, recording and in-transfer data are not automatically deleted",
          ],
          [
            "Full storage",
            "Pause capture if no eligible records can be cleared; sync and free space, then press REC or restart",
          ],
          [
            "Display status",
            "Wi-Fi, SD, duration, task, hand, power, width, force and synchronization status",
          ],
        ],
        note: "Do not remove the MicroSD card while recording or synchronizing. On transfer failure, retain connectivity for retry and check NAS permissions.",
      },
    ],
    delivery: [
      "Choose GB or GT for the experiment; confirm the GT bimanual kit contents.",
      "Verify NAS rsync service, permissions and task organization.",
      "Check each video, IMU, width and tactile stream; retain calibration and alignment reports.",
      "Evaluate tactile input against a fixed task baseline.",
    ],
    note: "Specifications follow the September 2026 v1.1 documentation. GT-specific tactile, weight, runtime and packaging figures are listed separately. Final configuration and acceptance follow the confirmed version.",
    related: ["thor-duo", "atlas-d", "fmg-imu"],
  },
  {
    slug: "fmg-imu",
    name: "FMG + IMU",
    category: "Capture",
    subtitle: "Forearm sensing for multimodal research",
    intro:
      "Pair forearm pressure patterns associated with muscle deformation and inertial motion with first-person video. A head-mounted camera and two forearm bands keep the palms and fingertips free for natural interaction.",
    image: "/assets/catalog/fmg-hero-cutout.webp",
    imageNote: "Wearing concept: head-mounted vision and one band on each forearm near the elbow.",
    status: "Research validation",
    metrics: [
      ["FMG + IMU", "Pressure patterns and motion"],
      ["Bilateral", "Left and right forearm capture"],
      ["Ego pairing", "First-person task context"],
    ],
    features: [
      [
        "Hands remain free",
        "Forearm bands support natural contact without covering the palms or fingertips. They do not replace every tactile or hand-pose sensor.",
      ],
      [
        "Complement visual observations",
        "A modular band combines inward-facing contact elements and an IMU. Forearm pressure changes are not measurements of fingertip-object force.",
      ],
      [
        "Test the value of each signal",
        "Gesture, 3D hand-pose and fingertip-pressure prediction are research directions requiring independent labels, cross-user testing and robot task comparisons.",
      ],
    ],
    groups: [
      {
        title: "Sensing architecture",
        rows: [
          ["Signals", "FMG + IMU; no EMG channels"],
          ["Form", "Modular forearm band with inward-facing contact elements"],
          [
            "Array",
            "Demonstration material labels a 10 x 4 three-dimensional force-sensing array; measurement points, axes and independent channel count require confirmation",
          ],
          [
            "Frequency",
            "120 Hz is stated in demonstration material; sampling, transmission refresh and algorithm output rates must be defined separately",
          ],
          ["IMU", "Axes, range, noise and sampling rate to be confirmed"],
          [
            "Wearable setup",
            "Head-mounted stereo vision + bilateral forearm bands; single / paired package scope to be confirmed",
          ],
          ["Communication", "Wireless protocol, bandwidth, range and SDK to be confirmed"],
        ],
        note: "Do not infer 40 or 120 data channels from the 10 x 4 label or the rendered module count.",
      },
      {
        title: "Data and evaluation",
        rows: [
          [
            "Raw records",
            "FMG outputs, inertial signals, timestamps, sequence numbers and quality flags; final schema requires interface confirmation",
          ],
          [
            "Derived outputs",
            "Model estimates of gestures, hand pose or fingertip pressure; dimensions, rate and accuracy require validation",
          ],
          [
            "Reference measurements",
            "Independent hand-pose, force or tactile sensors and annotations selected for the task",
          ],
          [
            "Cross-device alignment",
            "Clock mapping and synchronization events with Ego, UMI or robots; verify the error separately",
          ],
          [
            "Wear metadata",
            "Anonymous participant ID, hand, position, orientation, fit, resting baseline, date and re-wearing events",
          ],
          [
            "Export",
            "Session / episode organization with raw data, calibration and derived outputs; protocol and SDK licenses to be confirmed",
          ],
        ],
      },
      {
        title: "Engineering items to confirm",
        rows: [
          ["Sensing", "Range, accuracy, sensitivity, drift, hysteresis, overload and temperature effects"],
          [
            "Fit",
            "Forearm circumference, module adjustment, preload, long-session comfort and skin-contact materials",
          ],
          ["Power / structure", "Weight, battery, runtime, charging, protection and cleaning"],
          ["Link", "End-to-end latency, packet loss, coexistence, reconnect and device concurrency"],
          ["Software", "Operating systems, raw-data access, SDK, model licenses and user calibration"],
          ["Acceptance", "Independent users, cross-day re-wearing and matched task baselines"],
        ],
      },
    ],
    delivery: [
      "Freeze sensor and communication specifications before defining the schema.",
      "Calibrate fit and signal quality for each wearing session.",
      "Use independent reference sensors and store observations separately from predictions.",
      "Compare models with and without FMG under the same task and training budget.",
    ],
    note: "Research validation stage. Wearing concepts and demonstration videos are not test reports. FMG is not EMG or direct fingertip force ground truth. No unverified production, delivery or robot-performance claims are implied.",
    related: ["thor-duo", "umi", "data-glove"],
  },
  {
    slug: "data-glove",
    name: "Tactile Data Glove",
    category: "Capture",
    subtitle: "Contact sensing and electromagnetic tracking",
    intro:
      "Record where contact occurs as well as how the hand moves. Palm-side tactile arrays and fingertip electromagnetic pose tracking provide inputs for dexterous manipulation, teleoperation and vision-tactile research.",
    image: "/assets/catalog/tactile-glove-samples.webp",
    imageNote: "Sample appearance references; not a standard multi-glove package.",
    status: "Specification-based configuration",
    metrics: [
      ["360 points", "Standard tactile layout"],
      ["200 Hz", "Nominal tactile sampling"],
      ["180 Hz", "Nominal tracking output"],
    ],
    features: [
      [
        "Capture contact distribution",
        "Fabric piezoresistive arrays cover the main palm-side contact areas. Record raw ADC values and point mappings; force or pressure conversion requires separate calibration.",
      ],
      [
        "Reconstruct hand pose",
        "Five electromagnetic sensors track fingertip 6DoF poses. A 25DoF hand reconstruction is an algorithm output, not 25 independently measured joints.",
      ],
      [
        "Integrate the data streams",
        "USB and licensed SDKs provide access; wireless is optional. Validate coordinates, timing and robot end-effector mapping for each integration.",
      ],
    ],
    groups: [
      {
        title: "Standard and optional configuration",
        rows: [
          ["Tactile points", "360 standard; 600 optional with layout confirmation"],
          [
            "Sampling",
            "200 Hz standard; 400 Hz is a future upgrade direction, not a current delivery commitment",
          ],
          ["Connection / power", "USB standard; optional wireless receiver and battery module"],
          ["Package", "1 glove + 1 data cable; bilateral sets and tracking accessories specified separately"],
          [
            "Tracking accessories",
            "Confirm field generator / reference source, receiver, SDK licenses and cables",
          ],
          ["Customization", "Appearance, outer material and protection by agreement"],
        ],
      },
      {
        title: "Tactile array",
        rows: [
          ["Technology", "Flexible fabric piezoresistive sensing"],
          [
            "Standard layout",
            "24 points per finger (16 fingertip + 8 middle), plus 240 palm points (16 x 15)",
          ],
          ["Coverage", "Main palm-side regions except the four finger-base regions; confirm point map"],
          ["Nominal density", "9 points/cm2"],
          [
            "Trigger / maximum range",
            "20 g / 1500 g as stated in source documentation; loading conditions and force calibration require confirmation",
          ],
          ["Output", "Per-point ADC and total ADC; not directly N or Pa"],
          ["Repeatability / consistency", "Both +/-15% full scale"],
        ],
        note: "Contact area, stretch, fit, temperature and humidity affect readings. Preserve the baseline and recalibrate after re-wearing.",
      },
      {
        title: "Tracking and reconstruction",
        rows: [
          ["Sensors", "5 electromagnetic sensors; 6DoF each"],
          ["Output", "3-axis position and rotation per fingertip; nominal 180 Hz"],
          ["Hand reconstruction", "Algorithm-derived 25DoF pose"],
          [
            "0.2 mm figure",
            "Source table calls this accuracy; prose calls it resolution below 0.2 mm. Definition and test volume must be confirmed",
          ],
          [
            "Nominal latency",
            "10 ms wired / 30 ms wireless; measurement endpoints and conditions require confirmation",
          ],
          ["Deployment", "Confirm tracking volume, coordinates, field source and metal interference"],
        ],
        note: "Different sampling rates do not establish hardware synchronization. Sensor latency is not robot end-to-end control latency.",
      },
      {
        title: "Power, environment and SDK",
        rows: [
          ["Optional battery", "Nominal 1200 mAh; confirm selected module"],
          ["Runtime / charging", "Nominal 2.5 hours / 2 hours; conditions unspecified"],
          ["Temperature / humidity", "-15 to 60 C / 0-90% RH, nominal; not a waterproof rating"],
          [
            "Reliability source claim",
            "More than 500,000 cycles under a 10 kg impact test; fixture and conditions unconfirmed, not a glove payload rating",
          ],
          ["Care", "Avoid liquids, sharp objects and cable strain; clean with a soft dry cloth"],
          ["SDK", "Licensed Windows / macOS / Linux SDK; scope by agreement, not an open-source claim"],
          [
            "Integration",
            "Frame protocol, point map, calibration version, hand identity, timestamps and clock mapping",
          ],
        ],
      },
    ],
    delivery: [
      "Confirm hand, point layout, tracking accessories and communication configuration.",
      "Record the wearing baseline and verify reference coordinates and environmental interference.",
      "Check actual tactile and pose rates, dropped frames and latency separately.",
      "Archive raw ADC, calibrated measurements, estimated poses and mapped robot actions as distinct data layers.",
    ],
    note: "Based on specification V1.3 dated July 25, 2026. Figures are document claims, not Actrium measurements. Standard, optional, future and unresolved specifications are identified separately.",
    related: ["thor-quad", "umi", "atlas-a7"],
  },
  {
    slug: "atlas-r1",
    name: "Atlas R1",
    category: "Robotics",
    subtitle: "Open-frame wheeled humanoid research concept",
    intro:
      "An open-frame concept for mobile manipulation and bimanual learning. A circular wheeled base, vertical column and dual arms organize sensing, compute and actuation for integration, maintenance and research planning.",
    image: "/assets/catalog/atlas-r1-actrium-silver.webp",
    imageNote:
      "R1 concept with AI-inferred internal structure; not a physical prototype or engineering drawing.",
    status: "Research concept",
    metrics: [
      ["Wheeled", "Mobile manipulation"],
      ["Dual arm", "Bimanual research"],
      ["Open frame", "Concept architecture"],
    ],
    features: [
      [
        "Accessible structure",
        "Reduced decorative enclosure exposes conceptual supports and cable paths. Internal frame and electronics are inferred, not verified teardown details.",
      ],
      [
        "Mobile and bimanual architecture",
        "The base and vertical column organize side-mounted shoulder lift and arm placement. Travel, workspace and coordinated control require engineering validation.",
      ],
      [
        "Protection remains essential",
        "Joint housings, driver insulation, cable retention, pinch protection and emergency-stop design remain part of the engineering scope.",
      ],
    ],
    groups: [
      {
        title: "Concept scope",
        rows: [
          ["Version", "Open-frame concept V02; not engineering-frozen"],
          ["Retained layout", "Circular base, vertical column, shoulder lift, dual arms and grippers"],
          ["Inferred elements", "Internal frame, guide transmission, electronics and cable routing"],
          ["Arms / mobility", "Joint count, payload, precision, travel, speed and workspace to be confirmed"],
          ["Sensing", "Planned head and optional wrist cameras; models and timing to be confirmed"],
          ["Compute assessment", GPU_OPTIONS_NOTE],
          [
            "Safety",
            "Emergency stop, limits, communication-loss stop, collision controls and moving-part protection require validation",
          ],
        ],
      },
      {
        title: "Planned integration",
        rows: [
          ["Recording", "Vision, joints, end effectors, base state and task events"],
          [
            "Teleoperation",
            "Selected demonstration devices and supervised takeover; interfaces and latency to be confirmed",
          ],
          ["Learning workflow", "Planned cleaning, annotation, versioning and training export"],
          ["External devices", "Thor, UMI, tactile gloves and FMG + IMU pairing require validation"],
          ["Software scope", "SDK, protocols, ROS, URDF and simulation assets to be confirmed"],
          [
            "Engineering freeze",
            "Dimensions, mass, center of gravity, power, transport, battery, cooling, runtime and delivery scope remain open",
          ],
        ],
        note: "Open-frame describes the structure, not an open-source hardware or software license. Planned integration is not a currently available feature list.",
      },
    ],
    delivery: [
      "Define tasks, worksite and safety boundaries.",
      "Validate stiffness, center of gravity, wiring, thermal management and joint protection through engineering design.",
      "Confirm onboard versus external compute and its power and cooling requirements.",
      "Verify teleoperation, recording, policy deployment and safe fallback on agreed tasks.",
    ],
    note: "Research concept based on reference imagery and AI structural inference. No physical teardown was performed. The image cannot support fabrication, kinematic or safety validation; unconfirmed specifications are not performance or delivery commitments.",
    related: ["atlas-m", "data-glove", "thor-duo"],
  },
  {
    slug: "atlas-m",
    name: "Atlas M",
    category: "Robotics",
    subtitle: "Integrated mobile bimanual platform",
    intro:
      "Combine leader-follower arms, vision, compute and a mobile base in one system for mobile manipulation, bimanual demonstrations and imitation-learning experiments.",
    image: "/assets/catalog/atlas-m-actrium-silver.webp",
    imageNote: CONCEPT_IMAGE_NOTE,
    status: "Project configuration",
    metrics: [
      ["Dual arm", "Leader-follower teleoperation"],
      ["6 axes", "Reference manipulators"],
      ["4070-5090", "GPU configuration options"],
    ],
    features: [
      [
        "Integrated collection station",
        "Co-locate operator controls, execution and capture compute to reduce repeated installation and wiring.",
      ],
      [
        "Extend tasks beyond the desk",
        "A differential-drive base carries the dual-arm system for paired navigation and manipulation experiments.",
      ],
      [
        "Connect to policy research",
        "Mobile ALOHA and ACT are reference research workflows; data, training and deployment interfaces require project adaptation.",
      ],
    ],
    groups: [
      {
        title: "Reference system configuration",
        rows: [
          ["Base", "Two-wheel differential drive"],
          ["Arms", "Atlas A6 six-axis configuration"],
          ["Grippers / teaching devices", "Manufacturer-configured"],
          ["Depth camera", "Orbbec Dabai"],
          ["USB expansion", "4-port hub"],
          ["Frame dimensions", "1125 x 758 x 1507 mm"],
          ["Compute", GPU_OPTIONS_NOTE],
          ["Display", "11.6-inch, 1080P"],
          ["Keyboard", "Logitech"],
          ["Power", "Portable power station; four-outlet switched strip, 1.8 m cable"],
        ],
        note: "Accessory quantities, complete-system runtime, payload and control frequency are not fully specified and must not be inferred from other models.",
      },
    ],
    delivery: ATLAS_DELIVERY_CHECKLIST,
    note: ATLAS_CONFIG_NOTE,
    related: ["atlas-s", "atlas-d", "thor-duo"],
  },
  {
    slug: "atlas-s",
    name: "Atlas S",
    category: "Robotics",
    subtitle: "Modular mobile bimanual platform",
    intro:
      "Separate the operator station from the mobile execution system. The dual-arm platform supports experiments across operating heights and workspaces while the teleoperation station remains fixed.",
    image: "/assets/catalog/atlas-s-actrium-silver.webp",
    imageNote: CONCEPT_IMAGE_NOTE,
    status: "Project configuration",
    metrics: [
      ["4 arms", "Leader-follower setup"],
      ["2 m/s", "Maximum base speed"],
      ["IP54", "Base protection rating"],
    ],
    features: [
      [
        "Independent operator station",
        "Leader-arm pose mapping controls the follower system with separate space for the operator and robot.",
      ],
      [
        "Mobility and lift",
        "The reference system uses four-wheel drive and steering, a lifting arm structure, navigation and ROS lift drivers.",
      ],
      ["Spatial perception", "Three depth cameras and one lidar form the reference sensing configuration."],
    ],
    groups: [
      {
        title: "Mobile base",
        rows: [
          ["Structure", "Four-wheel drive and four-wheel steering"],
          ["Dimensions", "720 x 500 x 345 mm"],
          ["Base payload", "120 kg"],
          ["Maximum speed", "2 m/s"],
          ["Protection", "IP54"],
          ["Temperature", "-10 to 40 C"],
          ["Reference runtime", "3 hours fully loaded; 6 hours unloaded"],
        ],
        note: "These are base specifications, not arm payload or complete-system working runtime.",
      },
      {
        title: "Reference system",
        rows: [
          ["Height", "1322.4-1891.6 mm"],
          ["Dual-arm width", "757.7 mm"],
          ["Mobile platform width", "774.6 mm"],
          ["Arms", "4 x Atlas A6"],
          ["Sensing", "3 depth cameras / 1 lidar"],
          ["Compute", GPU_OPTIONS_NOTE],
          ["Display / keyboard", "11.6-inch / Logitech"],
          [
            "Power",
            "Portable power station; redundant battery and auto-charging dock inclusion to be confirmed",
          ],
        ],
      },
    ],
    delivery: ATLAS_DELIVERY_CHECKLIST,
    note: ATLAS_CONFIG_NOTE,
    related: ["atlas-m", "atlas-d", "umi"],
  },
  {
    slug: "atlas-d",
    name: "Atlas D",
    category: "Robotics",
    subtitle: "Desktop bimanual learning platform",
    intro:
      "Build a bimanual demonstration and execution environment on a standard desk, with a focus on grasping, coordinated manipulation and repeatable experiments.",
    image: "/assets/catalog/atlas-d-actrium-silver.webp",
    imageNote: CONCEPT_IMAGE_NOTE,
    status: "Project configuration",
    metrics: [
      ["4 arms", "Leader-follower setup"],
      ["1.5 kg", "Reference single-arm payload"],
      ["626 mm", "Reference single-arm reach"],
    ],
    features: [
      [
        "Desktop experiments",
        "Leader-follower bimanual hardware supports coordinated operation and demonstrations in a fixed workspace.",
      ],
      [
        "Modular installation",
        "Desktop mounts and fixtures support workstation changes; verify safety and calibration after installation.",
      ],
      [
        "Development access",
        "Reference interfaces and examples can support sensor and actuator extensions, subject to the delivered configuration.",
      ],
    ],
    groups: [
      {
        title: "Reference kit",
        rows: [
          ["Arms", "4 x Atlas A6"],
          ["Teaching devices", "2"],
          ["Depth cameras", "3"],
          ["Desktop mounting set", "1"],
          ["G-clamps", "8"],
        ],
      },
      {
        title: "Reference single-arm performance",
        rows: [
          ["Arm mass", "4.2 kg"],
          ["Payload", "1.5 kg"],
          ["Reach", "626 mm"],
          ["Repeatability", "+/-0.1 mm"],
          ["Compute options", GPU_OPTIONS_NOTE],
        ],
        note: "Single-arm payloads cannot be added to establish a bimanual payload rating. Camera models and compute inclusion require confirmation.",
      },
    ],
    delivery: ATLAS_DELIVERY_CHECKLIST,
    note: ATLAS_CONFIG_NOTE,
    related: ["research-arm", "atlas-a7", "atlas-m"],
  },
  {
    slug: "research-arm",
    name: "Atlas A6",
    category: "Robotics",
    subtitle: "Six-axis research manipulator",
    intro:
      "Start manipulation research with a single arm, then add vision, end effectors, demonstration devices and recording according to the task.",
    image: "/assets/catalog/atlas-a6-actrium-silver.webp",
    imageNote: CONCEPT_IMAGE_NOTE,
    status: "Project configuration",
    metrics: [
      ["6", "Degrees of freedom"],
      ["4.2 kg", "Arm mass"],
      ["+/-0.1 mm", "Repeatability"],
    ],
    features: [
      [
        "Compact research hardware",
        "A desktop mounting structure and integrated controller support small manipulation workstations.",
      ],
      [
        "Research toolchain",
        "Reference materials list Python, ROS 1 / ROS 2, URDF, Gazebo and Isaac Sim support. Confirm drivers and versions.",
      ],
      [
        "Separate commands from observations",
        "Record action commands, measured robot state and task outcomes separately. Commands are not execution ground truth.",
      ],
    ],
    groups: [
      {
        title: "Structure and performance",
        rows: [
          ["Degrees of freedom", "6"],
          ["Rated payload", "1.5 kg"],
          ["Arm mass", "4.2 kg"],
          ["Reach", "626 mm"],
          ["Repeatability", "+/-0.1 mm"],
          ["Power", "DC 24 V"],
          ["Base", "70 x 70 mm, 4 x M5"],
          ["Installation", "Desktop"],
          ["Materials", "Aluminum-alloy frame; plastic / resin enclosure"],
          ["Controller", "Integrated"],
        ],
      },
      {
        title: "Joint range and speed",
        columns: ["Joint", "Range", "Maximum speed"],
        rows: [
          ["J1", "-154 to 154 degrees", "180 deg/s"],
          ["J2", "0 to 195 degrees", "195 deg/s"],
          ["J3", "-175 to 0 degrees", "180 deg/s"],
          ["J4", "-100 to 112 degrees", "225 deg/s"],
          ["J5", "-75 to 75 degrees", "225 deg/s"],
          ["J6", "-170 to 170 degrees", "225 deg/s"],
        ],
      },
      {
        title: "Environment and development",
        rows: [
          ["Temperature", "-20 to 50 C"],
          ["Humidity", "25-85%, non-condensing"],
          ["Noise", "<60 dB"],
          ["Development", "Python, ROS 1 / ROS 2, URDF, Gazebo, Isaac Sim"],
          ["External compute", GPU_OPTIONS_NOTE],
        ],
      },
    ],
    delivery: ATLAS_DELIVERY_CHECKLIST,
    note: ATLAS_CONFIG_NOTE,
    related: ["atlas-a7", "atlas-d", "umi"],
  },
  {
    slug: "atlas-a7",
    name: "Atlas A7",
    category: "Robotics",
    subtitle: "Seven-axis lightweight research manipulator",
    intro:
      "A seven-degree-of-freedom arm for fixed-workstation manipulation and robot learning. Kinematic redundancy, hand-guided teaching and combined force-position control support motion planning, impedance-control and imitation-learning research. Select a left arm, right arm or bimanual setup.",
    image: "/assets/catalog/atlas-a7-gray-blue-v1.webp",
    imageNote:
      "Left / right color concepts; not physical product photographs. Single and dual-arm configurations are separate selections.",
    status: "Project configuration",
    metrics: [
      ["7 axes", "Redundant kinematics"],
      ["3 kg", "Nominal maximum payload"],
      ["739.45 mm", "Nominal maximum reach"],
    ],
    features: [
      [
        "More freedom for planning",
        "A seven-axis anthropomorphic arrangement supports end-effector orientation, joint obstacle avoidance and redundancy resolution. Nominal arm mass is 4.3 kg with +/-0.1 mm repeatability.",
      ],
      [
        "Hand-guided demonstration",
        "The manual lists gravity compensation, zero-force teaching and trajectory replay at a 100 Hz teaching sample rate. Joint torque-related readings are not direct end-effector contact forces.",
      ],
      [
        "Control and research integration",
        "Position, velocity, torque and MIT hybrid control are documented, alongside Python / C++, ROS 2 Humble, MoveIt2, Gazebo and URDF. Confirm interface versions.",
      ],
    ],
    groups: [
      {
        title: "Structure and performance",
        rows: [
          ["Degrees of freedom", "7"],
          ["Single-arm mass", "4.3 kg"],
          ["Maximum payload", "3 kg"],
          ["Maximum reach", "739.45 mm"],
          ["Repeatability", "+/-0.1 mm"],
          ["Materials", "Aluminum-alloy frame, plastic enclosure"],
          ["Mounting", "Rigid, level fixed workstation; hole pattern per version drawing"],
          ["Configurations", "Left / right / bimanual"],
        ],
        note: "Maximum payload depends on pose, tool mass, center of gravity and dynamics. Repeatability is not absolute positioning accuracy.",
      },
      {
        title: "Joint specifications",
        columns: ["Joint", "Range", "Maximum speed", "Rated torque", "Peak torque"],
        rows: [
          ["J1", "-60 to +180 degrees", "180 deg/s", "15 Nm", "30 Nm"],
          ["J2", "-180 to +15 degrees", "180 deg/s", "15 Nm", "30 Nm"],
          ["J3", "+/-158 degrees", "180 deg/s", "15 Nm", "30 Nm"],
          ["J4", "+/-110 degrees", "180 deg/s", "15 Nm", "30 Nm"],
          ["J5", "+/-158 degrees", "180 deg/s", "3 Nm", "7 Nm"],
          ["J6", "+/-90 degrees", "180 deg/s", "3 Nm", "7 Nm"],
          ["J7", "+/-158 degrees", "180 deg/s", "3 Nm", "7 Nm"],
        ],
        note: "Coordinates, zero positions and signs depend on arm side and driver version. Peak torque is not a continuous rating.",
      },
      {
        title: "Power, communication and control",
        rows: [
          ["System power", "DC 48 V; supply rated for at least 6 A with operating margin"],
          ["Combined power", "<300 W"],
          ["Communication", "CAN 2.0, 1 Mbps; USB-to-CAN adapter"],
          ["External ports", "1 power / 1 CAN"],
          ["Joint encoders", "21-bit dual encoders"],
          ["Control modes", "Position / velocity / torque / MIT hybrid"],
          ["Teaching", "Gravity compensation, zero-force recording, trajectory replay"],
          ["Teaching sample rate", "100 Hz"],
          [
            "Recorded fields",
            "Joint position, velocity and torque-related data; units and timestamps per SDK",
          ],
        ],
        note: "CAN bandwidth and teaching sample rate are not policy inference or end-to-end control frequency. Use the complete-system 48 V supply requirement.",
      },
      {
        title: "Software and configuration",
        rows: [
          ["Recommended environment", "Ubuntu 22.04 LTS"],
          ["SDK", "Python / C++, version-specific APIs and examples"],
          ["Middleware / planning", "ROS 2 Humble / MoveIt2"],
          ["Simulation", "Gazebo, URDF and matching controller configuration"],
          [
            "Compute",
            "External workstation or cloud service; GPU, models and trained skills not included by default",
          ],
          ["Temperature / humidity", "5-50 C / 35-80% RH, no frost"],
          ["Noise", "<60 dB; source measurement conditions unspecified"],
          ["Accessories", "USB-to-CAN, cables, fasteners and tools per bill of materials"],
          [
            "Confirm separately",
            "Power adapter, gripper, tactile end effector, cameras, fixtures, compute and safety equipment",
          ],
        ],
      },
    ],
    delivery: [
      "Freeze arm side, quantity, end effector, mounting, power and USB-to-CAN configuration.",
      "Provide agreed drivers, SDK examples, models, version notes and license scope.",
      "Verify zero positions, limits, emergency stop and communication-loss handling. Enable the proper mode for hand-guided teaching; do not force an unpowered or braked arm.",
      "Accept recording, replay and tasks under defined installation, load and speed conditions.",
    ],
    note: "Nominal manual specifications, not Actrium measurements. Atlas A7 is a project product name. Force control alone does not establish safe shared human-robot operation. Validate the complete installation and agreed tasks.",
    related: ["research-arm", "atlas-d", "data-glove"],
  },
];

export const getProduct = (e) => PRODUCTS.find((t) => t.slug === e);

export const PRODUCT_GROUPS = [
  {
    name: "Ego Thor",
    path: "/products/thor-ego",
    slugs: ["thor-duo", "thor-quad", "thor-hex"],
  },
  {
    name: "Multimodal Capture",
    slugs: ["umi", "data-glove", "fmg-imu"],
  },
  {
    name: "Atlas Robotics",
    slugs: ["research-arm", "atlas-a7", "atlas-d", "atlas-m", "atlas-s", "atlas-r1"],
  },
];
